import React, { useState, useEffect } from 'react';
import uploadToDropbox from '../../../services/dropboxService';

const inputStyle = {
  background: '#0d0d0d',
  border: '1px solid #2a2a2a',
  color: 'white',
};

const inputFocusStyle = {
  borderColor: 'rgba(220,38,38,0.6)',
  boxShadow: '0 0 0 2px rgba(220,38,38,0.1)',
  outline: 'none',
};

function ArtistForm({ initialData, onSubmit, onCancel, title = 'Add Artist' }) {
  const [formData, setFormData] = useState({ name: '', bio: '', country: '', artist_image_url: '', artist_id: '' });
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [focusedField, setFocusedField] = useState(null);

  useEffect(() => {
    if (initialData) {
      setFormData({
        artist_id: initialData.artist_id || '',
        name: initialData.name || '',
        bio: initialData.bio || '',
        country: initialData.country || '',
        artist_image_url: initialData.artist_image_url || ''
      });
    }
  }, [initialData]);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (formData.name.length > 255) newErrors.name = 'Name must be less than 255 characters';
    if (formData.country && formData.country.length > 100) newErrors.country = 'Country must be less than 100 characters';
    return newErrors;
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/jpg'];
    if (!validImageTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, artist_image_url: 'Please select a valid image file (JPEG, PNG, GIF, WEBP)' }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, artist_image_url: 'Image size must be less than 5MB' }));
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const response = await uploadToDropbox(file, (progress) => setUploadProgress(progress));
      let imageUrl;
      if (typeof response === 'string') imageUrl = response;
      else if (response?.download_url) imageUrl = response.download_url;
      else if (response?.url) imageUrl = response.url;
      else throw new Error('Unexpected response format from upload service');
      setFormData(prev => ({ ...prev, artist_image_url: imageUrl }));
      setErrors(prev => ({ ...prev, artist_image_url: '' }));
    } catch (error) {
      setErrors(prev => ({ ...prev, artist_image_url: 'Failed to upload image. Please try again.' }));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) handleFileUpload(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) { setErrors(validationErrors); return; }
    onSubmit({
      ...formData,
      artist_image_url: formData.artist_image_url || null,
      country: formData.country || null,
      bio: formData.bio || null,
      artist_id: formData.artist_id || null

    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const getFieldStyle = (fieldName) => ({
    ...inputStyle,
    ...(focusedField === fieldName ? inputFocusStyle : {}),
    ...(errors[fieldName] ? { borderColor: '#dc2626' } : {}),
  });

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: '#111', border: '1px solid #1f1f1f' }}>
      <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #1a1a1a', background: '#0d0d0d' }}>
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#dc2626' }}>
            <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 className="text-base font-black text-white uppercase tracking-wide">{title}</h2>
        </div>
        <button onClick={onCancel} style={{ color: '#6b7280' }} className="hover:text-white transition-colors">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6">
        <div className="space-y-5">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#9ca3af' }}>Name *</label>
            <input
              type="text" name="name" value={formData.name} onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg text-sm text-white placeholder-gray-600 transition-all"
              style={getFieldStyle('name')}
              onFocus={() => setFocusedField('name')}
              onBlur={() => setFocusedField(null)}
              placeholder="Enter artist name"
            />
            {errors.name && <p className="mt-1 text-xs" style={{ color: '#f87171' }}>{errors.name}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#9ca3af' }}>Country</label>
            <input
              type="text" name="country" value={formData.country} onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg text-sm text-white placeholder-gray-600 transition-all"
              style={getFieldStyle('country')}
              onFocus={() => setFocusedField('country')}
              onBlur={() => setFocusedField(null)}
              placeholder="Enter country"
            />
            {errors.country && <p className="mt-1 text-xs" style={{ color: '#f87171' }}>{errors.country}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#9ca3af' }}>Artist Image</label>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <input type="file" accept="image/*" onChange={handleFileSelect} disabled={uploading} className="hidden" id="artist-image-upload" />
                <label
                  htmlFor="artist-image-upload"
                  className="px-4 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition-all"
                  style={{
                    background: uploading ? '#1a1a1a' : 'rgba(220,38,38,0.1)',
                    border: '1px solid rgba(220,38,38,0.25)',
                    color: uploading ? '#6b7280' : '#f87171',
                    cursor: uploading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {uploading ? 'Uploading...' : 'Choose Image'}
                </label>
                {uploading && (
                  <div className="flex-1">
                    <div className="h-1 rounded-full overflow-hidden" style={{ background: '#1a1a1a' }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${uploadProgress * 100}%`, background: '#dc2626' }} />
                    </div>
                    <p className="text-xs mt-1" style={{ color: '#6b7280' }}>{Math.round(uploadProgress * 100)}%</p>
                  </div>
                )}
              </div>

              {formData.artist_image_url && !uploading && (
                <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: '#0d0d0d', border: '1px solid #1a1a1a' }}>
                  <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0" style={{ background: '#1a1a1a' }}>
                    <img src={formData.artist_image_url} alt="Artist preview" className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/56?text=ERR'; }} />
                  </div>
                  <div className="flex-1">
                    <span className="text-xs font-medium" style={{ color: '#4ade80' }}>✓ Image uploaded</span>
                  </div>
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, artist_image_url: '' }))} style={{ color: '#f87171' }}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {errors.artist_image_url && <p className="text-xs" style={{ color: '#f87171' }}>{errors.artist_image_url}</p>}
              <p className="text-xs" style={{ color: '#4b5563' }}>Square image, min 300×300px, max 5MB (JPEG, PNG, GIF, WEBP)</p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#9ca3af' }}>Bio</label>
            <textarea
              name="bio" value={formData.bio} onChange={handleChange} rows="4"
              className="w-full px-4 py-3 rounded-lg text-sm text-white placeholder-gray-600 transition-all resize-none"
              style={getFieldStyle('bio')}
              onFocus={() => setFocusedField('bio')}
              onBlur={() => setFocusedField(null)}
              placeholder="Enter artist biography"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            type="submit" disabled={uploading}
            className="flex-1 py-3 rounded-lg font-bold text-white text-sm transition-all hover:opacity-90"
            style={{ background: uploading ? '#1a1a1a' : '#dc2626', cursor: uploading ? 'not-allowed' : 'pointer' }}
          >
            {initialData ? 'Update Artist' : 'Create Artist'}
          </button>
          <button type="button" onClick={onCancel} className="flex-1 py-3 rounded-lg text-sm font-medium transition-all" style={{ background: '#0d0d0d', color: '#9ca3af', border: '1px solid #2a2a2a' }}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default ArtistForm;